<template>
	<!-- Special: Checkbox -->
	<template v-if="type === 'checkbox'">
		<div class="form-group" :class="{ 'is-invalid': error }">
			<div class="form-check form-checkbox">
				<input class="form-check-input" type="checkbox" role="switch" :id="field" v-model="model" v-bind="attributes" :value />
				<label class="form-check-label" :for="field">
					<span v-html="label"></span>
					<slot />
				</label>
			</div>

			<!-- Error -->
			<div class="invalid-feedback">
				<IconExclamationCircle size="18" />
				<span>{{ error }}</span>
			</div>
		</div>
	</template>

	<!-- Special: Radio -->
	<template v-else-if="type === 'radio'">
		<div class="form-check form-radio">
			<input class="form-check-input" type="radio" role="switch" :id="field" v-model="model" :value v-bind="attributes" />
			<label class="form-check-label" :for="field">
				{{ label }}
				<slot />
			</label>
		</div>
	</template>

	<!-- Special: Radio card -->
	<template v-else-if="type === 'radio-card'">
		<div class="form-check form-radio-card" :class="[{ 'has-ribbon': ribbon }, { 'is-not-clickable': !clickable }, size]">
			<input class="form-check-input" type="radio" :name="field" :id="field" v-model="model" :value />
			<label class="form-check-label" :for="field">
				<div class="ribbon" v-if="ribbon">{{ ribbon }}</div>
				<div class="content">
					<slot />
				</div>
			</label>
		</div>
	</template>

	<!-- Special: Switch-->
	<template v-else-if="type === 'switch'">
		<div class="form-check form-switch" :class="{ 'is-flipped': flipped }">
			<label class="form-check-label" for="flexSwitchCheckDefault">{{ label }}</label>
			<input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault" v-model="model" v-bind="attributes" />
		</div>
	</template>

	<!-- Others -->
	<template v-else>
		<div
			class="form-group"
			:class="[
				{ 'has-icon': icon },
				{ 'form-floating': variant === 'floating' },
				{ 'has-textarea': type === 'textarea' },
				{ 'is-invalid': error },
			]"
		>
			<!-- Icon -->
			<component :is="icon" v-if="icon" size="20" class="icon" />

			<!-- Text-->
			<template v-if="type === 'text' || type === 'email'">
				<input
					:type
					class="form-control"
					v-model="model"
					:placeholder="variant === 'floating' ? ' ' : placeholder"
					v-bind="attributes"
					:disabled
				/>
			</template>

			<!-- Textarea -->
			<template v-if="type === 'textarea'">
				<textarea
					class="form-control form-control-textarea"
					v-model="model"
					:id="field"
					:placeholder="variant === 'floating' ? ' ' : placeholder"
					v-bind="attributes"
				></textarea>
			</template>

			<!-- Password -->
			<template v-if="type === 'password'">
				<input
					type="password"
					class="form-control"
					v-model="model"
					:placeholder="variant === 'floating' ? ' ' : placeholder"
					v-bind="attributes"
				/>
			</template>

			<!-- Date -->
			<template v-if="type === 'date'">
				<input
					type="date"
					class="form-control"
					v-model="model"
					:placeholder="variant === 'floating' ? ' ' : placeholder"
					v-bind="attributes"
				/>
			</template>

			<!-- Select -->
			<template v-if="type === 'select'">
				<select v-model="model" class="form-control" placeholder="" v-bind="attributes" @change="$emit('change', model)" :disabled>
					<option :value="null" v-if="placeholder">{{ placeholder }}</option>
					<option v-for="option in options" :value="option.value ?? option?.id" :key="option.value ?? option.id">{{ option.name }}</option>
				</select>
			</template>

			<!-- Label -->
			<label v-if="label">{{ label }}</label>

			<!-- Error -->
			<div class="invalid-feedback">
				<IconExclamationCircle size="18" />
				<span>{{ error }}</span>
			</div>
		</div>
	</template>
</template>

<script setup>
import { watch } from 'vue';
import { IconExclamationCircle } from '@tabler/icons-vue';

const emit = defineEmits(['change']);

const props = defineProps({
	type: {
		type: String,
		required: false,
		default: 'text',
	},

	field: {
		type: String,
		required: false,
	},

	placeholder: {
		type: String,
		required: false,
	},

	icon: {
		type: Function,
		required: false,
	},

	variant: {
		type: String,
		required: false,
	},

	label: {
		type: String,
		required: false,
	},

	options: {
		type: Object,
		required: false,
	},

	error: {
		type: String,
		required: false,
	},

	attributes: {
		type: Object,
		default: () => ({}),
	},

	value: {
		type: [String, Number],
		required: false,
	},

	clickable: {
		type: Boolean,
		required: false,
		default: true,
	},

	flipped: {
		type: Boolean,
		default: false,
	},

	disabled: {
		type: Boolean,
		default: false,
	},

	size: {
		type: String,
		required: false,
	},

	ribbon: {
		type: String,
		required: false,
	},
});

const model = defineModel();

watch(model, (newValue) => {
	emit('change', newValue);
});
</script>
